import { __awaiter } from "tslib";
import { SequenceReference, AnnotationReference } from "@rcsb/rcsb-api-tools/lib/RcsbGraphQL/Types/Borrego/GqlTypes";
import { RcsbFvAbstractModule } from "./RcsbFvAbstractModule";
import * as acm from "../../RcsbAnnotationConfig/RcsbAnnotationConfig.ac.json";
import { buriedResidues, buriedResiduesFilter } from "../../RcsbUtils/TrackGenerators/BuriedResidues";
import { burialFraction, burialFractionFilter } from "../../RcsbUtils/TrackGenerators/BurialFraction";
import { FeatureType } from "../../RcsbExport/FeatureType";
import { rcsbRequestCtxManager } from "../../RcsbRequest/RcsbRequestContextManager";
import { SequenceTrackFactory } from "../RcsbFvFactories/RcsbFvTrackFactory/TrackFactoryImpl/SequenceTrackFactory";
import { InstanceSequenceTrackTitleFactory } from "../RcsbFvFactories/RcsbFvTrackFactory/TrackTitleFactoryImpl/InstanceSequenceTrackTitleFactory";
import { Assertions } from "../../RcsbUtils/Helpers/Assertions";
var assertDefined = Assertions.assertDefined;
import { TagDelimiter } from "@rcsb/rcsb-api-tools/lib/RcsbUtils/TagDelimiter";
const annotationConfigMap = acm;
export class RcsbFvInterface extends RcsbFvAbstractModule {
    protectedBuild() {
        return __awaiter(this, void 0, void 0, function* () {
            var _a, _b, _c, _d, _e;
            const buildConfig = this.buildConfig;
            const instanceId = buildConfig.instanceId;
            assertDefined(instanceId);
            this.instanceId = instanceId;
            const source = [AnnotationReference.PdbEntity, AnnotationReference.PdbInstance, AnnotationReference.Uniprot, AnnotationReference.PdbInterface];
            const alignmentRequestContext = {
                queryId: instanceId,
                from: SequenceReference.PdbInstance,
                to: SequenceReference.Uniprot,
                externalTrackBuilder: (_a = buildConfig.additionalConfig) === null || _a === void 0 ? void 0 : _a.externalTrackBuilder
            };
            const alignmentResponse = yield this.alignmentCollector.collect(alignmentRequestContext, (_b = buildConfig.additionalConfig) === null || _b === void 0 ? void 0 : _b.alignmentFilter);
            yield this.buildAlignmentTracks(alignmentRequestContext, alignmentResponse, {
                sequenceTrackFactory: new SequenceTrackFactory(this.getPolymerEntityInstanceTranslator(), new InstanceSequenceTrackTitleFactory(this.getPolymerEntityInstanceTranslator()))
            });
            const annotationsRequestContext = {
                queryId: instanceId,
                reference: SequenceReference.PdbInstance,
                titleSuffix: this.titleSuffix((_c = buildConfig === null || buildConfig === void 0 ? void 0 : buildConfig.additionalConfig) === null || _c === void 0 ? void 0 : _c.rcsbContext).bind(this),
                trackTitle: this.trackTitle.bind(this),
                typeSuffix: this.typeSuffix.bind(this),
                sources: source,
                annotationGenerator: interfaceAnnotations,
                annotationFilter: filter,
                rcsbContext: (_d = buildConfig.additionalConfig) === null || _d === void 0 ? void 0 : _d.rcsbContext,
                externalTrackBuilder: (_e = buildConfig.additionalConfig) === null || _e === void 0 ? void 0 : _e.externalTrackBuilder
            };
            const annotationsFeatures = yield this.annotationCollector.collect(annotationsRequestContext);
            yield this.buildAnnotationsTrack(annotationsRequestContext, annotationsFeatures, annotationConfigMap);
            this.boardConfigData.length = yield this.alignmentCollector.getAlignmentLength();
            this.boardConfigData.includeAxis = true;
            return void 0;
        });
    }
    concatAlignmentAndAnnotationTracks() {
        var _a;
        const buildConfig = this.buildConfig;
        this.rowConfigData =
            !((_a = buildConfig.additionalConfig) === null || _a === void 0 ? void 0 : _a.hideAlignments) ?
                [this.referenceTrack].concat(this.alignmentTracks).concat(this.annotationTracks)
                :
                    this.alignmentTracks.concat(this.annotationTracks);
    }
    typeSuffix(ann, d) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a;
            if (ann.source === AnnotationReference.PdbInterface && ((_a = ann.target_identifiers) === null || _a === void 0 ? void 0 : _a.interface_id)) {
                return ann.target_identifiers.interface_id + "|" + ann.target_identifiers.interface_partner_index;
            }
        });
    }
    titleSuffix(rcsbContext) {
        return ((ann, d) => __awaiter(this, void 0, void 0, function* () {
            var _a;
            if (ann.source === AnnotationReference.PdbInterface) {
                if (!ann.target_id || !ann.target_identifiers)
                    return "";
                const interfaceTranslate = yield rcsbRequestCtxManager.getInterfaceToInstance(ann.target_id);
                const chain = this.instanceId.split(TagDelimiter.instance)[1];
                const interfaceId = TagDelimiter.getInterfaceId(ann.target_identifiers);
                const chPair = interfaceTranslate.getInstances(interfaceId);
                if (!chPair)
                    return "";
                const asym = chPair[0] == chain ? chPair[1] : chPair[0];
                const auth = (yield rcsbRequestCtxManager.getEntityToInstance(this.instanceId.split(TagDelimiter.instance)[0])).translateAsymToAuth(asym);
                const operators = interfaceTranslate.getOperatorIds(interfaceId);
                let partnerOperator = "";
                const partner_idx = (_a = ann.target_identifiers) === null || _a === void 0 ? void 0 : _a.interface_partner_index;
                if (operators && typeof partner_idx == "number" && rcsbContext && Array.isArray(rcsbContext.operatorIds)) {
                    const opIndex = operators[partner_idx].map(o => o.join("-")).indexOf(rcsbContext.operatorIds.join("-"));
                    if (opIndex < 0) {
                        console.error(`Operator Id ${rcsbContext.operatorIds.join("-")} not found in [[${operators[0]}],[${operators[1]}]]`);
                        console.error(ann.target_identifiers);
                    }
                    else {
                        partnerOperator = TagDelimiter.operatorComposition + operators[1 - partner_idx][opIndex].join(TagDelimiter.operatorComposition);
                    }
                }
                return (asym == auth ? asym : `${asym}[auth ${auth}]`) + partnerOperator;
            }
        }));
    }
    trackTitle(ann, d) {
        return __awaiter(this, void 0, void 0, function* () {
            if (ann.source === AnnotationReference.PdbInterface && d.type === FeatureType.BurialFraction) {
                return "BINDING CHAIN ";
            }
        });
    }
}
export function interfaceAnnotations(annotations) {
    const buried = buriedResidues(annotations);
    const burial = burialFraction(annotations);
    return new Promise(resolve => {
        const out = new Array();
        if (buried && buried.length > 0)
            out.push(...buried);
        if (burial && burial.length > 0)
            out.push(...burial);
        resolve(out);
    });
}
export function filter(ann) {
    return new Promise(resolve => {
        resolve(burialFractionFilter(buriedResiduesFilter(ann)));
    });
}
//# sourceMappingURL=RcsbFvInterface.js.map